import {
  afilhadoCheckupsSchema,
  checkupSchema,
  checkupTopicSchema,
  type AfilhadoCheckups,
  type Checkup,
  type CheckupStatus,
  type CheckupTopic,
} from '@growup/streaming-shared';
import { Button, Checkbox, Field, Notice, StatusBadge, Textarea } from '@growup/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { getJson, messageOf, send } from '../api';
import { formatDate } from '../format';
import { CHECKUP_LOOK, formatMinutes, TOPIC_LABEL } from './labels';

const STATUSES: CheckupStatus[] = ['ok', 'attention', 'unreachable'];

/**
 * O relatorio do padrinho (GS2): três perguntas, menos de um minuto. The
 * Twitch numbers for the period come filled in; previous reports below.
 */
export function CheckupForm({
  streamerId,
  onSent,
}: {
  streamerId: string;
  onSent?: (data: AfilhadoCheckups) => void;
}) {
  const path = `/api/streaming/padrinho/afilhados/${streamerId}/checkups`;
  const [data, setData] = useState<AfilhadoCheckups | null>(null);
  const [status, setStatus] = useState<CheckupStatus | null>(null);
  const [topics, setTopics] = useState<CheckupTopic[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const sentRef = useRef(onSent);
  sentRef.current = onSent;

  const load = useCallback(() => {
    getJson(path, afilhadoCheckupsSchema)
      .then(setData)
      .catch((e: unknown) => setMessage({ tone: 'danger', text: messageOf(e) }));
  }, [path]);
  useEffect(load, [load]);

  const toggle = (t: CheckupTopic, on: boolean) =>
    setTopics((list) => (on ? [...list, t] : list.filter((x) => x !== t)));

  const submit = async () => {
    if (status === null) return;
    setBusy(true);
    setMessage(null);
    try {
      const next = afilhadoCheckupsSchema.parse(
        await send('POST', path, { status, topics: status === 'attention' ? topics : [], note }),
      );
      setData(next);
      setStatus(null);
      setTopics([]);
      setNote('');
      setMessage({ tone: 'success', text: 'Relatório entregue. Obrigado!' });
      sentRef.current?.(next);
    } catch (e) {
      setMessage({ tone: 'danger', text: messageOf(e) });
    } finally {
      setBusy(false);
    }
  };

  const ready = status !== null && (status !== 'attention' || topics.length > 0);

  return (
    <div
      className="flex flex-col gap-3 rounded-sm border border-gu-line p-3"
      aria-label="Relatório"
    >
      {data ? (
        <p className="text-caption text-gu-ink-muted">
          Desde {formatDate(data.period.from)}: {data.period.sessions} transmissões,{' '}
          {formatMinutes(data.period.minutesLive)}
          {' · '}Última:{' '}
          {data.period.lastStreamAt ? formatDate(data.period.lastStreamAt) : 'nenhuma'}
        </p>
      ) : null}
      <fieldset className="flex flex-col gap-2">
        <legend className="font-semibold">Como está?</legend>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={status === s ? 'primary' : 'secondary'}
              aria-pressed={status === s}
              onClick={() => setStatus(s)}
            >
              {CHECKUP_LOOK[s].label}
            </Button>
          ))}
        </div>
      </fieldset>
      {status === 'attention' ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="font-semibold">Em quê?</legend>
          {checkupTopicSchema.options.map((t) => (
            <Checkbox
              key={t}
              label={TOPIC_LABEL[t]}
              checked={topics.includes(t)}
              onChange={(e) => toggle(t, e.target.checked)}
            />
          ))}
        </fieldset>
      ) : null}
      <Field label="Nota" optional hint="Só o Stream Staff e os padrinhos a veem.">
        <Textarea
          value={note}
          rows={2}
          maxLength={2000}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
      <div>
        <Button size="sm" disabled={busy || !ready} onClick={() => void submit()}>
          Entregar relatório
        </Button>
      </div>
      {data && data.items.length > 0 ? <CheckupList items={data.items.slice(0, 3)} /> : null}
    </div>
  );
}

/** Os relatórios de um afilhado (padrinho e Stream Staff). */
export function CheckupList({ items }: { items: Checkup[] }) {
  return (
    <ul className="flex flex-col gap-2" aria-label="Relatórios anteriores">
      {items.map((c) => (
        <li key={c.id} className="flex flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2 text-caption text-gu-ink-muted">
            <StatusBadge tone={CHECKUP_LOOK[c.status].tone}>
              {CHECKUP_LOOK[c.status].label}
            </StatusBadge>
            {formatDate(c.createdAt)} · {c.padrinho}
            {c.topics.length > 0 ? ` · ${c.topics.map((t) => TOPIC_LABEL[t]).join(', ')}` : ''}
            {` · ${String(c.period.sessions)} transmissões, ${formatMinutes(c.period.minutesLive)}`}
          </span>
          {c.note ? <span className="whitespace-pre-line">{c.note}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/** O histórico de relatórios na ficha do streamer (Stream Staff). */
export function CheckupHistory({ streamerId }: { streamerId: string }) {
  const [items, setItems] = useState<Checkup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    getJson(`/api/streaming/streamers/${streamerId}/checkups`, z.array(checkupSchema))
      .then(setItems)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [streamerId]);
  if (error) return <Notice tone="danger">{error}</Notice>;
  if (items === null) return null;
  if (items.length === 0) return <p className="text-gu-ink-muted">Ainda sem relatórios.</p>;
  return <CheckupList items={items} />;
}
